import React, { useState, useEffect } from 'react';
import { hostelAPI } from '../../services/api';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import Modal from '../../components/common/Modal';
import toast from 'react-hot-toast';
import { formatDate } from '../../utils/helpers';

const HostelManagement = () => {
  const [tab, setTab] = useState('rooms');
  const [hostels, setHostels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [messMenu, setMessMenu] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedHostel, setSelectedHostel] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      hostelAPI.hostels(),
      hostelAPI.rooms(),
      hostelAPI.allocations({ is_active: true }),
      hostelAPI.visitors(),
      hostelAPI.messMenu(),
    ]).then(([hRes, rRes, aRes, vRes, mRes]) => {
      if (hRes.status === 'fulfilled') setHostels(hRes.value.data.results || hRes.value.data);
      if (rRes.status === 'fulfilled') setRooms(rRes.value.data.results || rRes.value.data);
      if (aRes.status === 'fulfilled') setAllocations(aRes.value.data.results || aRes.value.data);
      if (vRes.status === 'fulfilled') setVisitors(vRes.value.data.results || vRes.value.data);
      if (mRes.status === 'fulfilled') setMessMenu(mRes.value.data.results || mRes.value.data);
    }).finally(() => setLoading(false));
  }, []);

  const roomColumns = [
    { key: 'room_number', label: 'Room No.', render: v => <strong>{v}</strong> },
    { key: 'hostel_name', label: 'Hostel' },
    { key: 'room_type', label: 'Type', render: v => <span className="badge bg-info text-dark">{v}</span> },
    { key: 'floor', label: 'Floor' },
    { key: 'capacity', label: 'Capacity' },
    { key: 'occupied', label: 'Occupied', render: (v, row) => <span style={{ color: v >= row.capacity ? '#ef4444' : '#10b981', fontWeight: 600 }}>{v}/{row.capacity}</span> },
    { key: 'monthly_rent', label: 'Rent/Month', render: v => `₹${v}` },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
  ];

  const allocationColumns = [
    { key: 'student_name', label: 'Student', render: v => <strong>{v}</strong> },
    { key: 'hostel_name', label: 'Hostel' },
    { key: 'room_number', label: 'Room' },
    { key: 'allocation_date', label: 'From', render: v => formatDate(v) },
    { key: 'is_active', label: 'Status', render: v => <StatusBadge status={v ? 'active' : 'inactive'} /> },
  ];

  const visitorColumns = [
    { key: 'visitor_name', label: 'Visitor', render: v => <strong>{v}</strong> },
    { key: 'student_name', label: 'Visiting' },
    { key: 'relation', label: 'Relation' },
    { key: 'purpose', label: 'Purpose' },
    { key: 'visit_date', label: 'Date', render: v => formatDate(v) },
    { key: 'in_time', label: 'In Time' },
    { key: 'out_time', label: 'Out Time', render: v => v || 'Still in' },
  ];

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const meals = ['breakfast', 'lunch', 'snacks', 'dinner'];

  return (
    <div>
      <div className="page-header">
        <div><h2>Hostel Management</h2><p>Rooms, allocations, mess, and visitor records</p></div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary" onClick={() => setShowModal(true)}>🏠 Allocate Room</button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>👥 Add Visitor</button>
        </div>
      </div>

      <div className="row g-3 mb-4">
        {[
          { icon: '🏠', label: 'Total Rooms', value: rooms.length, bg: '#eff6ff', color: '#1d4ed8' },
          { icon: '✅', label: 'Occupied', value: rooms.filter(r => r.status === 'occupied').length, bg: '#fff7ed', color: '#c2410c' },
          { icon: '🛏️', label: 'Available', value: rooms.filter(r => r.status === 'available').length, bg: '#f0fdf4', color: '#15803d' },
          { icon: '👤', label: 'Students Allotted', value: allocations.length, bg: '#faf5ff', color: '#7c3aed' },
        ].map(s => (
          <div key={s.label} className="col-6 col-lg-3">
            <div className="stat-card">
              <div className="stat-icon" style={{ background: s.bg, color: s.color, fontSize: 22 }}>{s.icon}</div>
              <div className="stat-info"><h3>{s.value}</h3><p>{s.label}</p></div>
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex gap-2 mb-4 flex-wrap">
        {[['rooms','🛏️ Rooms'],['allocations','👤 Allocations'],['mess','🍽️ Mess Menu'],['visitors','👥 Visitors']].map(([k,l]) => (
          <button key={k} className={`btn ${tab === k ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'rooms' && (
        <div>
          <div className="mb-3">
            <select className="form-select w-auto" value={selectedHostel} onChange={e => setSelectedHostel(e.target.value)}>
              <option value="">All Hostels</option>
              {hostels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </div>
          <div className="card">
            <div className="card-header"><span>🛏️ Rooms ({rooms.length})</span></div>
            <DataTable columns={roomColumns} data={rooms} loading={loading} />
          </div>
        </div>
      )}

      {tab === 'allocations' && (
        <div className="card">
          <div className="card-header"><span>👤 Room Allocations ({allocations.length})</span></div>
          <DataTable columns={allocationColumns} data={allocations} loading={loading} />
        </div>
      )}

      {tab === 'mess' && (
        <div className="card">
          <div className="card-header"><span>🍽️ Weekly Mess Menu</span></div>
          <div className="card-body">
            {messMenu.length === 0 ? (
              <div className="empty-state"><div className="empty-icon">🍽️</div><h5>No Menu Set</h5><p>Add weekly mess menu for the hostel.</p></div>
            ) : (
              <div className="table-responsive">
                <table className="table">
                  <thead><tr><th>Day</th>{meals.map(m => <th key={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</th>)}</tr></thead>
                  <tbody>
                    {days.map(day => (
                      <tr key={day}>
                        <td><strong>{day}</strong></td>
                        {meals.map(meal => {
                          const item = messMenu.find(m => m.day === day && m.meal === meal);
                          return <td key={meal} style={{ fontSize: 13 }}>{item?.menu || '—'}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'visitors' && (
        <div className="card">
          <div className="card-header">
            <span>👥 Visitor Records</span>
            <button className="btn btn-sm btn-primary">➕ Add Visitor</button>
          </div>
          <DataTable columns={visitorColumns} data={visitors} loading={loading} />
        </div>
      )}
    </div>
  );
};
export default HostelManagement;
